import { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { submitAdInquiryApi } from "api/inquiry";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  red: "#EF4444",
  amber50: "#FFFBEB",
  amber800: "#92400E",
};

const AD_TYPES = ["배너 광고", "협업 제안", "기타"];

const LIMITS = {
  company: 30,
  name: 30,
  email: 50,
  message: 300,
};

// 한국 휴대폰/유선번호 자동 하이픈 (최대 11자리)
function formatPhone(input) {
  const digits = (input || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.startsWith("02")) {
    if (digits.length < 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length < 10)
      return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export default function AdInquiryDialog({ open, onClose }) {
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [adType, setAdType] = useState(AD_TYPES[0]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // 다이얼로그 열릴 때마다 초기화
  useEffect(() => {
    if (open) {
      setCompany("");
      setName("");
      setPhone("");
      setEmail("");
      setAdType(AD_TYPES[0]);
      setMessage("");
      setError("");
      setDone(false);
    }
  }, [open]);

  const submit = async () => {
    setError("");
    if (!company.trim() || !name.trim() || !phone.trim() || !email.trim() || !message.trim()) {
      setError("모든 필수 항목을 입력해주세요.");
      return;
    }
    if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(email.trim())) {
      setError("이메일 형식이 올바르지 않습니다.");
      return;
    }
    setLoading(true);
    const r = await submitAdInquiryApi({
      company: company.trim(),
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
      adType,
      message: message.trim(),
    });
    setLoading(false);
    if (r?.success) {
      setDone(true);
    } else {
      setError(r?.error || "전송에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <ReactModal
      isOpen={open}
      onRequestClose={loading ? undefined : onClose}
      shouldCloseOnOverlayClick={!loading}
      style={{
        overlay: { zIndex: 200, backgroundColor: "rgba(0,0,0,0.45)" },
        content: {
          fontFamily:
            'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
          zIndex: 250,
          position: "absolute",
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
          width: "min(640px, 94vw)",
          maxHeight: "94vh",
          overflowY: "auto",
          background: "#fff",
          border: "none",
          borderRadius: 18,
          padding: 0,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        },
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 22px 14px",
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>
            광고 문의하기
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: C.sub }}>
            담당자가 영업일 기준 1~2일 내 연락드립니다.
          </div>
        </div>
        <button
          type="button"
          onClick={loading ? undefined : onClose}
          aria-label="닫기"
          style={{
            background: "transparent",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            color: C.sub,
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
          }}
        >
          ✕
        </button>
      </div>

      {done ? (
        <div
          style={{
            padding: "28px 22px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 9999,
              background: C.greenBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 32,
            }}
          >
            ✅
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.ink }}>
            문의가 접수되었습니다
          </div>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
            관심 가져 주셔서 감사합니다.<br />
            영업일 기준 1~2일 내 담당자가 연락드립니다.
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: 8,
              padding: "10px 22px",
              borderRadius: 10,
              background: C.greenDark,
              color: "#fff",
              border: "none",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            확인
          </button>
        </div>
      ) : (
        <>
          <div
            style={{
              padding: "16px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", gap: 10 }}>
              <Field
                label="회사 / 단체명"
                required
                counter={`${company.length}/${LIMITS.company}`}
                style={{ flex: 1 }}
              >
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value.slice(0, LIMITS.company))}
                  maxLength={LIMITS.company}
                  placeholder="예) 미솔루션"
                  disabled={loading}
                  style={inputStyle}
                />
              </Field>
              <Field
                label="담당자"
                required
                counter={`${name.length}/${LIMITS.name}`}
                style={{ flex: 1 }}
              >
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, LIMITS.name))}
                  maxLength={LIMITS.name}
                  placeholder="이름"
                  disabled={loading}
                  style={inputStyle}
                />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Field label="연락처" required style={{ flex: 1 }}>
                <input
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="010-0000-0000"
                  disabled={loading}
                  inputMode="numeric"
                  style={inputStyle}
                />
              </Field>
              <Field
                label="이메일"
                required
                counter={`${email.length}/${LIMITS.email}`}
                style={{ flex: 1 }}
              >
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value.slice(0, LIMITS.email))}
                  maxLength={LIMITS.email}
                  placeholder="example@company.com"
                  disabled={loading}
                  inputMode="email"
                  style={inputStyle}
                />
              </Field>
            </div>
            <Field label="광고 유형">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {AD_TYPES.map((t) => {
                  const active = adType === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAdType(t)}
                      disabled={loading}
                      style={{
                        padding: "7px 14px",
                        borderRadius: 9999,
                        fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        color: active ? "#fff" : C.body,
                        background: active ? C.greenDark : "#fff",
                        border: `1px solid ${active ? C.greenDark : C.line}`,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field
              label="문의 내용"
              required
              counter={`${message.length}/${LIMITS.message}`}
            >
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, LIMITS.message))}
                maxLength={LIMITS.message}
                placeholder="간단한 광고 목적 / 노출 기간 / 예산 등을 자유롭게 작성해주세요."
                disabled={loading}
                rows={4}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: 96,
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                }}
              />
            </Field>

            {error && (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "#FEF2F2",
                  border: "1px solid #FECACA",
                  color: "#991B1B",
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            style={{
              padding: "14px 22px 18px",
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              borderTop: `1px solid ${C.line}`,
              background: "#FAFAFA",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                background: "#fff",
                border: `1px solid ${C.line}`,
                color: C.body,
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              취소
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              style={{
                padding: "10px 22px",
                borderRadius: 10,
                background: loading ? "#9CA3AF" : C.greenDark,
                color: "#fff",
                border: "none",
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "전송 중..." : "문의 전송"}
            </button>
          </div>
        </>
      )}
    </ReactModal>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  fontSize: 14,
  color: C.ink,
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

function Field({ label, children, required, counter, style }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 12,
          color: C.body,
          fontWeight: 600,
        }}
      >
        <span>
          {label} {required && <span style={{ color: "#EF4444" }}>*</span>}
        </span>
        {counter && (
          <span style={{ fontSize: 11, color: C.sub, fontWeight: 500 }}>
            {counter}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
